/**
 * Vault schemas
 *
 * Validation schemas for Vault feature - document storage and management.
 */

import { z } from 'zod';

// ═══════════════════════════════════════════════════════════════════════
// FILE UPLOAD SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

// ── L4 region: F39/N17 vault size limit ──
/**
 * The largest file the vault accepts, in bytes (100 MB).
 *
 * One number for the three places that enforce it: the presign request's
 * `fileSize` cap and the upload proxy's multer limit in platform-api
 * (`vault.controller.ts`), and the SDK's `uploadDirect`, which refuses a larger
 * file with a typed `UploadTooLargeError` BEFORE it mints a row or sends a byte —
 * a citizen whose phone video is 400 MB is told "too large", not "upload failed".
 */
export const VAULT_MAX_FILE_BYTES = 100 * 1024 * 1024;
// ── end L4 region ──

/**
 * Document category enum
 */
export const documentCategorySchema = z.enum([
  'identity', // Passport, ID card
  'visa', // Visa documents
  'travel', // Flight tickets, travel insurance
  'employment', // Work contracts, offer letters
  'education', // Diplomas, certificates
  'financial', // Bank statements, tax documents
  'housing', // Rental agreements, utility bills
  'health', // Health insurance, medical records
  'legal', // Legal documents, notarizations
  'personal', // Personal documents
  'other',
]);

/**
 * Document status enum
 */
export const documentStatusSchema = z.enum([
  'pending', // Awaiting processing
  'processing', // Being processed
  'verified', // Verified by system/staff
  'rejected', // Rejected (issue found)
  'expired', // Document expired
]);

/**
 * Request upload URL schema
 */
export const requestUploadUrlSchema = z.object({
  filename: z.string().min(1).max(255),
  mimeType: z.string().refine((type) => {
    const allowed = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/heic',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    return allowed.includes(type);
  }, 'File type not allowed'),
  size: z
    .number()
    .int()
    .positive()
    .max(50 * 1024 * 1024), // 50MB max
  category: documentCategorySchema,
  journeyInstanceId: z.uuid().optional(),
});

/**
 * Confirm upload schema
 */
export const confirmUploadSchema = z.object({
  fileId: z.uuid(),
  uploadKey: z.string(),
  metadata: z
    .object({
      documentType: z.string().max(100).optional(),
      issuingCountry: z.string().length(2).optional(), // ISO country code
      issueDate: z.coerce.date().optional(),
      expiryDate: z.coerce.date().optional(),
      documentNumber: z.string().max(100).optional(),
      notes: z.string().max(1000).optional(),
    })
    .optional(),
});

/**
 * Update file metadata schema
 */
export const updateFileMetadataSchema = z.object({
  fileId: z.uuid(),
  category: documentCategorySchema.optional(),
  metadata: z
    .object({
      documentType: z.string().max(100).optional(),
      issuingCountry: z.string().length(2).optional(),
      issueDate: z.coerce.date().optional(),
      expiryDate: z.coerce.date().optional(),
      documentNumber: z.string().max(100).optional(),
      notes: z.string().max(1000).optional(),
    })
    .optional(),
  tags: z.array(z.string().max(50)).max(10).optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// FILE LISTING SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * List files schema
 */
export const listFilesSchema = z.object({
  category: documentCategorySchema.optional(),
  status: documentStatusSchema.optional(),
  journeyInstanceId: z.uuid().optional(),
  search: z.string().max(200).optional(),
  tags: z.array(z.string()).optional(),
  fromDate: z.coerce.date().optional(),
  toDate: z.coerce.date().optional(),
  sortBy: z.enum(['name', 'date', 'size', 'category']).default('date'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
});

/**
 * View file schema
 */
export const viewFileSchema = z.object({
  fileId: z.uuid(),
});

/**
 * Download file schema
 */
export const downloadFileSchema = z.object({
  fileId: z.uuid(),
  inline: z.boolean().default(false), // View in browser vs download
});

// ═══════════════════════════════════════════════════════════════════════
// FILE MANAGEMENT SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Delete file schema
 */
export const deleteFileSchema = z.object({
  fileId: z.uuid(),
  permanent: z.boolean().default(false), // Soft delete by default
});

/**
 * Restore file schema
 */
export const restoreFileSchema = z.object({
  fileId: z.uuid(),
});

/**
 * Move file schema
 */
export const moveFileSchema = z.object({
  fileId: z.uuid(),
  newCategory: documentCategorySchema,
});

/**
 * Duplicate file schema
 */
export const duplicateFileSchema = z.object({
  fileId: z.uuid(),
  newFilename: z.string().min(1).max(255).optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// FILE SHARING SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Share file schema
 */
export const shareFileSchema = z.object({
  fileId: z.uuid(),
  expiresIn: z.number().int().min(1).max(168).default(24), // Hours (max 7 days)
  password: z.string().min(6).max(50).optional(),
  allowDownload: z.boolean().default(true),
  maxViews: z.number().int().min(1).max(100).optional(),
});

/**
 * Revoke share schema
 */
export const revokeShareSchema = z.object({
  shareId: z.uuid(),
});

// ═══════════════════════════════════════════════════════════════════════
// STORAGE SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Storage usage schema (response validation)
 */
export const storageUsageSchema = z.object({
  used: z.number().int().min(0),
  limit: z.number().int().min(0),
  percentage: z.number().min(0).max(100),
  byCategory: z.record(documentCategorySchema, z.number().int().min(0)).optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════

export type DocumentCategory = z.infer<typeof documentCategorySchema>;
export type DocumentStatus = z.infer<typeof documentStatusSchema>;
export type RequestUploadUrlInput = z.infer<typeof requestUploadUrlSchema>;
export type ConfirmUploadInput = z.infer<typeof confirmUploadSchema>;
export type UpdateFileMetadataInput = z.infer<typeof updateFileMetadataSchema>;
export type ListFilesInput = z.infer<typeof listFilesSchema>;
export type ViewFileInput = z.infer<typeof viewFileSchema>;
export type DownloadFileInput = z.infer<typeof downloadFileSchema>;
export type DeleteFileInput = z.infer<typeof deleteFileSchema>;
export type RestoreFileInput = z.infer<typeof restoreFileSchema>;
export type MoveFileInput = z.infer<typeof moveFileSchema>;
export type DuplicateFileInput = z.infer<typeof duplicateFileSchema>;
export type ShareFileInput = z.infer<typeof shareFileSchema>;
export type RevokeShareInput = z.infer<typeof revokeShareSchema>;
export type StorageUsage = z.infer<typeof storageUsageSchema>;
